import { useCallback } from 'react';
import dayjs from 'dayjs';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useNotification } from '@/hooks';
import { getErrorMessage } from '@/utils/errorUtils';
import type { DriverApi, EditDriverSchema } from '../models';
import { useUpdateDriverMutation,  } from '../services';
import { useUpdateDriverLegacyNumberMutation } from '../services/drivers.service';

export type UpdateBusinessDriverOptions = {
  existingDriver: DriverApi;
  onSuccess?: () => void;
};

const useDriverActions = () => {
  const { showNotification } = useNotification();
  const [updateDriver, { isLoading: isUpdatingDriver }] =
    useUpdateDriverMutation();
  const [updateLegacyNumber, { isLoading: isUpdatingLegacyNumber }] =
    useUpdateDriverLegacyNumberMutation();

  const onUpdateDriver = useCallback(
    async (
      driverId: string,
      data: EditDriverSchema,
      options: UpdateBusinessDriverOptions
    ) => {
      const existing = options.existingDriver;

      try {
        const updatePromises: Promise<unknown>[] = [
          updateDriver({
            id: driverId,
            body: {
              firstName: data.firstName.trim(),
              lastName: data.lastName.trim(),
              email: data.email.trim(),
              documentNumber: data.documentNumber.trim(),
              birthDate: data.birthDate
                ? dayjs(data.birthDate).format('YYYY-MM-DD')
                : null,
              workPhoneNumber: data.workPhoneNumber?.trim() || null,
              personalPhoneNumber: data.personalPhoneNumber?.trim() || null,
              status: existing.status,
              ...(existing.documentTypeId !== undefined && {
                documentTypeId: existing.documentTypeId,
              }),
              ...(existing.workStatusId !== undefined && {
                workStatusId: existing.workStatusId,
              }),
              ...(existing.currentOperatorId !== undefined && {
                currentOperatorId: existing.currentOperatorId,
              }),
              ...(existing.addressId !== undefined && {
                addressId: existing.addressId,
              }),
              ...(existing.externalId !== undefined && {
                externalId: existing.externalId,
              }),
            },
          }).unwrap()
        ];

        if (data.legacyNumber?.trim() !== (existing.legacyNumber || '')) {
          updatePromises.push(
            updateLegacyNumber({
              id: driverId,
              legacyNumber: data.legacyNumber?.trim() || '',
            }).unwrap()
          );
        }

        await Promise.all(updatePromises);

        showNotification(
          'Conductor actualizado correctamente',
          NotificationSeverity.success
        );
        options?.onSuccess?.();
      } catch (error: unknown) {
        console.error('Error al actualizar conductor:', error);
        showNotification(
          getErrorMessage(
            error,
            'No se pudo actualizar el conductor. Intentá de nuevo.'
          ),
          NotificationSeverity.error
        );
      }
    },
    [updateDriver, updateLegacyNumber, showNotification]
  );

  const isAnyUpdating = isUpdatingDriver || isUpdatingLegacyNumber;

  return {
    onUpdateDriver,
    isUpdatingDriver: isAnyUpdating,
    isLoading: isAnyUpdating,
  };
};


export default useDriverActions;
